import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { translateMenuTerm, translateMenuTermList } from '@/i18n/translateMenuContent';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getProductIncludedItems } from '@/lib/productComposition';
import { ProductAttributeIcons } from '@/components/products/ProductAttributeIcons';
import { ProductFavoriteHeart } from '@/components/products/ProductFavoriteHeart';
import { onDemoProductImageError, DEMO_TAG_LABELS } from '@/lib/demo/demoBusiness';
import { cn } from '@/lib/utils';
import { getMenuCardStyle, getMenuMutedTextStyle, isPremiumLightMenu } from '@/lib/menuTheme';
import {
  getPublicCatalogDescription,
  groupCatalogProductsByCategory,
  partitionCatalogFeaturedItems,
  shouldGroupCatalogByCategory,
} from './catalogHelpers';
import { getCatalogSectionTitleClass } from '@/lib/menuTheme';
import { getCatalogPromoTypeLabel } from '@/lib/menu/catalogPromos';
import {
  MENU_LAYOUT_CLASSIC,
  MENU_LAYOUT_GRID,
  MENU_LAYOUT_LIST,
  getMenuLayout,
} from '@/lib/menuLayout';

function ProductPrice({ product, accent, standard, premium, bizForTheme, tc, size = 'text-xl' }) {
  if (product.discount_percentage > 0) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('font-bold', size)} style={{ color: accent }}>
          ${product.final_price?.toLocaleString()}
        </span>
        <span
          className={cn('text-sm line-through', standard && tc.productMuted)}
          style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
        >
          ${product.price?.toLocaleString()}
        </span>
        <Badge variant="destructive" className="text-xs">
          -{product.discount_percentage}%
        </Badge>
      </div>
    );
  }
  if (product.compare_at_price) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('font-bold', size)} style={{ color: accent }}>
          ${(product.final_price ?? product.price)?.toLocaleString()}
        </span>
        <span
          className={cn('text-sm line-through', standard && tc.productMuted)}
          style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
        >
          ${product.compare_at_price.toLocaleString()}
        </span>
      </div>
    );
  }
  return (
    <span className={cn('font-bold', size)} style={{ color: accent }}>
      ${product.price?.toLocaleString()}
    </span>
  );
}

function CatalogProductCard({
  product,
  onOpenProduct,
  highlightedPromoId,
  bizForTheme,
  standard,
  premium,
  pl,
  branding: b,
  accent,
  tc,
  layout = MENU_LAYOUT_CLASSIC,
  isLiked = false,
  onToggleLike,
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const cardIncluded = translateMenuTermList(getProductIncludedItems(product), lng);
  const cardDesc = translateMenuTerm(getPublicCatalogDescription(product), lng);
  const displayName = translateMenuTerm(product.name, lng);
  const isPromoCard = Boolean(product.is_catalog_promo);
  const isHighlighted =
    isPromoCard && highlightedPromoId && product.catalog_promo_id === highlightedPromoId;
  const isGrid = layout === MENU_LAYOUT_GRID;
  const isList = layout === MENU_LAYOUT_LIST;

  const cardShell = {
    className: cn(
      'relative overflow-hidden transition-all duration-200 cursor-pointer',
      standard && tc.productCard,
      standard && 'hover:shadow-xl hover:-translate-y-0.5',
      premium && 'shadow-sm hover:shadow-md',
      product.is_daily_deal && standard && 'ring-2 ring-amber-400 ring-offset-2',
      product.is_bestseller && standard && 'ring-2 ring-orange-400 ring-offset-2',
      isPromoCard && standard && 'ring-2 ring-violet-400 ring-offset-2',
      isHighlighted && 'ring-2 ring-violet-600 ring-offset-2 animate-pulse',
      isList && 'flex flex-row items-stretch',
    ),
    style: premium
      ? {
          ...getMenuCardStyle(bizForTheme),
          ...(product.is_daily_deal ? { outline: `2px solid ${accent}`, outlineOffset: 2 } : {}),
          ...(product.is_bestseller ? { outline: `2px solid #ea580c`, outlineOffset: 2 } : {}),
          ...(isPromoCard ? { outline: `2px solid ${accent}`, outlineOffset: 2 } : {}),
        }
      : undefined,
    onClick: () => onOpenProduct(product),
    'data-testid': isPromoCard
      ? `promo-card-${product.catalog_promo_id}`
      : `product-card-${product.id}`,
    'data-promo-id': isPromoCard ? product.catalog_promo_id : undefined,
    'data-layout': layout,
  };

  if (isList) {
    return (
      <Card {...cardShell}>
        <div
          className={cn(
            'shrink-0 overflow-hidden relative',
            product.photo_url ? 'w-24 sm:w-28' : 'w-20',
          )}
        >
          {product.photo_url ? (
            <img
              src={product.photo_url}
              alt={displayName}
              className="h-full min-h-[5.5rem] w-full object-cover"
              onError={(e) => onDemoProductImageError(e, bizForTheme)}
            />
          ) : (
            <div
              className="flex h-full min-h-[5.5rem] items-center justify-center text-xs"
              style={premium ? { backgroundColor: b.card_border_color, color: b.muted_text_color } : undefined}
            >
              —
            </div>
          )}
          <ProductFavoriteHeart
            absolute
            size="sm"
            liked={isLiked}
            onToggle={() => onToggleLike?.(product.id)}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-center p-3 sm:p-4">
          <div className="flex items-start justify-between gap-2">
            <h3
              className={cn('font-semibold text-base leading-snug', standard && tc.productTitle)}
              style={premium ? { color: b.text_color } : undefined}
            >
              {displayName}
            </h3>
            <ProductPrice
              product={product}
              accent={accent}
              standard={standard}
              premium={premium}
              bizForTheme={bizForTheme}
              tc={tc}
              size="text-base"
            />
          </div>
          {cardDesc ? (
            <p
              className={cn('mt-1 text-sm line-clamp-2', standard && tc.productMuted)}
              style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
            >
              {cardDesc}
            </p>
          ) : null}
          {product.availability === 'out_of_stock' && (
            <Badge variant="secondary" className="mt-2 w-fit">
              {t('catalog.outOfStock')}
            </Badge>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card {...cardShell}>
      {product.photo_url && (
        <div
          className={cn(
            'overflow-hidden relative',
            isGrid ? 'mx-auto mt-4 h-28 w-28 rounded-full' : 'w-full h-48',
          )}
        >
          <img
            src={product.photo_url}
            alt={displayName}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.04]"
            onError={(e) => onDemoProductImageError(e, bizForTheme)}
          />
          {!isGrid && product.is_bestseller && !product.is_daily_deal && (
            <Badge
              className="absolute top-2 left-2 bg-orange-500 text-white text-xs"
              data-testid="bestseller-badge"
            >
              {t('catalog.bestseller')}
            </Badge>
          )}
          {!isGrid && product.is_daily_deal && (
            <Badge
              className="absolute top-2 left-2 bg-amber-500 text-white text-xs"
              data-testid="daily-deal-badge"
            >
              {t('catalog.dailyDealBadge')}
            </Badge>
          )}
          {!isGrid && isPromoCard && (
            <Badge className="absolute top-2 left-2 bg-violet-600 text-white text-xs">
              {getCatalogPromoTypeLabel(product.catalog_promo_type)}
            </Badge>
          )}
          {!isGrid &&
          bizForTheme?.is_demo &&
          product.demo_featured &&
          !product.is_daily_deal &&
          !product.is_bestseller &&
          !isPromoCard ? (
            <Badge className="absolute top-2 left-2 bg-violet-600 text-white text-[10px] uppercase tracking-wide">
              {t('catalog.featured')}
            </Badge>
          ) : null}
          <ProductFavoriteHeart
            absolute
            size={isGrid ? 'sm' : 'md'}
            liked={isLiked}
            onToggle={() => onToggleLike?.(product.id)}
          />
        </div>
      )}
      {!product.photo_url ? (
        <div className={cn('absolute top-2 right-2 z-10', isGrid && 'right-3 top-3')}>
          <ProductFavoriteHeart
            size="sm"
            liked={isLiked}
            onToggle={() => onToggleLike?.(product.id)}
          />
        </div>
      ) : null}
      <div className={cn(isGrid ? 'p-3 text-center' : 'p-4 md:p-5')}>
        {bizForTheme?.is_demo && Array.isArray(product.demo_tags) && product.demo_tags.length > 0 ? (
          <div className={cn('flex flex-wrap gap-1 mb-2', isGrid && 'justify-center')}>
            {product.demo_tags.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className={cn(
                  'text-[10px] uppercase tracking-wide',
                  tag === 'spicy' && 'border-orange-400/60 text-orange-700 dark:text-orange-300',
                  tag === 'premium' && 'border-violet-400/60 text-violet-700 dark:text-violet-300',
                  tag === 'bestseller' && 'border-amber-500/50 text-amber-800 dark:text-amber-200',
                  tag === 'nuevo' && 'border-emerald-500/50 text-emerald-800 dark:text-emerald-200',
                )}
              >
                {DEMO_TAG_LABELS[tag] || tag}
              </Badge>
            ))}
          </div>
        ) : null}
        <ProductAttributeIcons
          attributes={product.attributes}
          className={cn('mb-2', isGrid && 'justify-center')}
          size="sm"
          stopPropagation
        />
        {product.is_bestseller && !product.is_daily_deal && !product.photo_url && (
          <Badge className="bg-orange-500 text-white text-xs mb-2" data-testid="bestseller-badge">
            {t('catalog.bestseller')}
          </Badge>
        )}
        {product.is_daily_deal && !product.photo_url && (
          <Badge className="bg-amber-500 text-white text-xs mb-2" data-testid="daily-deal-badge">
            {t('catalog.dailyDealBadge')}
          </Badge>
        )}
        {isPromoCard && !product.photo_url && (
          <Badge className="bg-violet-600 text-white text-xs mb-2">
            {getCatalogPromoTypeLabel(product.catalog_promo_type)}
          </Badge>
        )}
        <h3
          className={cn(
            'font-semibold mb-1',
            isGrid ? 'text-sm line-clamp-2' : 'text-lg',
            standard && tc.productTitle,
          )}
          style={premium ? { color: b.text_color } : undefined}
        >
          {displayName}
        </h3>
        {!isGrid ? (
          <div className="mb-3 space-y-1">
            {cardIncluded.length > 0 && (
              <p className="text-sm">
                <span
                  className={cn('font-medium', standard && tc.productTitle)}
                  style={premium ? { color: b.text_color } : undefined}
                >
                  {t('catalog.comesWith')}{' '}
                </span>
                <span
                  className={cn(standard && tc.productMuted)}
                  style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
                >
                  {cardIncluded.join(', ')}
                </span>
              </p>
            )}
            {cardDesc ? (
              <p
                className={cn('text-sm line-clamp-2', standard && tc.productMuted)}
                style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
              >
                {cardDesc}
              </p>
            ) : null}
          </div>
        ) : null}
        <div className={cn('flex items-center gap-2', isGrid ? 'justify-center' : 'justify-between pr-12')}>
          <ProductPrice
            product={product}
            accent={accent}
            standard={standard}
            premium={premium}
            bizForTheme={bizForTheme}
            tc={tc}
            size={isGrid ? 'text-base' : 'text-xl'}
          />
        </div>
        {product.availability === 'out_of_stock' && (
          <Badge variant="secondary" className="mt-2">
            {t('catalog.outOfStock')}
          </Badge>
        )}
      </div>
      {premium && !isGrid && (
        <div
          className="pointer-events-none absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full border-2 shadow-md"
          style={{
            borderColor: accent,
            color: accent,
            backgroundColor: pl ? '#FFFFFF' : b.card_background_color,
          }}
          aria-hidden
        >
          <Plus className="h-5 w-5" />
        </div>
      )}
    </Card>
  );
}

function CatalogProductGridLayout({ children, layout = MENU_LAYOUT_CLASSIC }) {
  const className =
    layout === MENU_LAYOUT_GRID
      ? 'grid grid-cols-2 gap-3 sm:gap-4 md:gap-6'
      : layout === MENU_LAYOUT_LIST
        ? 'grid grid-cols-1 gap-3 md:gap-4 max-w-2xl mx-auto'
        : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8';
  return (
    <div className={className} data-testid={`catalog-product-layout-${layout}`}>
      {children}
    </div>
  );
}

function CatalogSectionTitle({ children, standard, premium, tc, branding, className }) {
  const titleClass = getCatalogSectionTitleClass(null, standard);
  return (
    <h2
      className={cn(
        'text-xl md:text-2xl font-bold mb-5 md:mb-6',
        titleClass,
        standard && tc.productTitle,
        className,
      )}
      style={premium ? { color: branding.text_color } : undefined}
    >
      {children}
    </h2>
  );
}

export default function CatalogProductGrid({
  products,
  categories = [],
  menuHasNoProducts,
  searchQuery,
  selectedCategory,
  onClearFilters,
  onOpenProduct,
  highlightedPromoId,
  bizForTheme,
  standard,
  premium,
  pl: plProp,
  branding: b,
  accent,
  tc,
  layout: layoutProp,
  isProductLiked,
  onToggleProductLike,
}) {
  const { t, i18n } = useTranslation();
  const pl = plProp ?? (premium ? isPremiumLightMenu(bizForTheme) : false);
  const layout = layoutProp || getMenuLayout(bizForTheme);
  /** En portada de categorías, al entrar a una sección usamos grilla visual. */
  const productLayout =
    layout === 'categories' ? MENU_LAYOUT_GRID : layout;
  const groupByCategory =
    productLayout !== MENU_LAYOUT_LIST &&
    productLayout !== MENU_LAYOUT_GRID &&
    shouldGroupCatalogByCategory(selectedCategory, searchQuery);

  const { featured, regular } = useMemo(() => {
    if (!groupByCategory) return { featured: [], regular: products };
    return partitionCatalogFeaturedItems(products);
  }, [groupByCategory, products]);

  const sections = useMemo(() => {
    if (!groupByCategory) return null;
    return groupCatalogProductsByCategory(regular, categories);
  }, [groupByCategory, regular, categories]);

  const cardProps = {
    onOpenProduct,
    highlightedPromoId,
    bizForTheme,
    standard,
    premium,
    pl,
    branding: b,
    accent,
    tc,
    layout: productLayout,
    onToggleLike: onToggleProductLike,
  };

  return (
    <>
      {groupByCategory ? (
        <>
          {featured.length > 0 ? (
            <section className="mb-10 md:mb-12" data-testid="catalog-category-section-__featured__">
              <CatalogSectionTitle standard={standard} premium={premium} tc={tc} branding={b}>
                {t('catalog.featuredSection')}
              </CatalogSectionTitle>
              <CatalogProductGridLayout layout={productLayout}>
                {featured.map((product) => (
                  <CatalogProductCard
                    key={product.id}
                    product={product}
                    isLiked={Boolean(isProductLiked?.(product.id))}
                    {...cardProps}
                  />
                ))}
              </CatalogProductGridLayout>
            </section>
          ) : null}

          {sections?.length ? (
            <div className="space-y-10 md:space-y-12">
              {sections.map((section, index) => (
                <section key={section.id} data-testid={`catalog-category-section-${section.id}`}>
                  <CatalogSectionTitle
                    standard={standard}
                    premium={premium}
                    tc={tc}
                    branding={b}
                    className={index > 0 ? 'pt-2' : undefined}
                  >
                    {translateMenuTerm(section.name, i18n.language)}
                  </CatalogSectionTitle>
                  <CatalogProductGridLayout layout={productLayout}>
                    {section.items.map((product) => (
                      <CatalogProductCard
                        key={product.id}
                        product={product}
                        isLiked={Boolean(isProductLiked?.(product.id))}
                        {...cardProps}
                      />
                    ))}
                  </CatalogProductGridLayout>
                </section>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <CatalogProductGridLayout layout={productLayout}>
          {products.map((product) => (
            <CatalogProductCard
              key={product.id}
              product={product}
              isLiked={Boolean(isProductLiked?.(product.id))}
              {...cardProps}
            />
          ))}
        </CatalogProductGridLayout>
      )}

      {products.length === 0 && (
        <div className="text-center py-12 space-y-4">
          {menuHasNoProducts ? (
            <>
              <h3
                className={cn('text-lg font-semibold', standard && tc.productTitle)}
                style={premium ? { color: b.text_color } : undefined}
              >
                {t('catalog.emptyMenuTitle')}
              </h3>
              <p
                className={cn('text-sm max-w-md mx-auto', standard && tc.emptyHint)}
                style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
              >
                {t('catalog.emptyMenuBody')}
              </p>
            </>
          ) : (
            <p
              className={cn(standard && tc.emptyHint)}
              style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
            >
              {t('catalog.noResultsTitle')}
            </p>
          )}
          {(searchQuery || selectedCategory !== 'all') && (
            <Button
              variant="outline"
              style={
                premium
                  ? {
                      borderColor: b.card_border_color,
                      color: b.text_color,
                      backgroundColor: b.card_background_color,
                    }
                  : undefined
              }
              onClick={onClearFilters}
            >
              {t('catalog.seeAllProducts')}
            </Button>
          )}
        </div>
      )}
    </>
  );
}
